import { useState } from "react";
import { ChevronDown, ChevronRight, Trash2, Fuel, Zap, Database, Thermometer, Cloud, Factory } from "lucide-react";

const IncineratorEmissionAnalytics = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const calculations = [
    {
      id: "incinerator-co2-emission",
      title: "Incinerator CO₂ Emission Calculation",
      description: "Calculates CO₂ emissions for each fuel type using CF_CO₂ factors from FuelTable",
      formula: "CO₂ = Fuel Consumption (kg) × CF_CO₂ (kg/kg-fuel)",
      subFormulas: [
        "CO₂_HFO = incineratorConsHfo × CF_CO₂_HFO",
        "CO₂_LFO = incineratorConsLfo × CF_CO₂_LFO", 
        "CO₂_MGO = incineratorConsMgo × CF_CO₂_MGO",
        "Total CO₂ = CO₂_HFO + CO₂_LFO + CO₂_MGO"
      ],
      icon: <Cloud className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "incinerator-ch4-emission",
      title: "Incinerator Methane (CH₄) Emission Calculation",
      description: "Calculates CH₄ emissions for each fuel type using CF_CH₄ factors from FuelTable",
      formula: "CH₄ = Fuel Consumption (kg) × CF_CH₄ (kg/kg-fuel)",
      subFormulas: [
        "CH₄_HFO = incineratorConsHfo × CF_CH₄_HFO",
        "CH₄_LFO = incineratorConsLfo × CF_CH₄_LFO",
        "CH₄_MGO = incineratorConsMgo × CF_CH₄_MGO",
        "Total CH₄ = CH₄_HFO + CH₄_LFO + CH₄_MGO"
      ],
      icon: <Factory className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "incinerator-n2o-emission",
      title: "Incinerator Nitrous Oxide (N₂O) Emission Calculation",
      description: "Calculates N₂O emissions for each fuel type using CF_N₂O factors from FuelTable",
      formula: "N₂O = Fuel Consumption (kg) × CF_N₂O (kg/kg-fuel)",
      subFormulas: [
        "N₂O_HFO = incineratorConsHfo × CF_N₂O_HFO",
        "N₂O_LFO = incineratorConsLfo × CF_N₂O_LFO",
        "N₂O_MGO = incineratorConsMgo × CF_N₂O_MGO",
        "Total N₂O = N₂O_HFO + N₂O_LFO + N₂O_MGO"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "well-to-tank-calculation",
      title: "Incinerator Well-to-Tank (WtT) Calculation",
      description: "Calculates upstream emissions from incinerator fuel production and transportation",
      formula: "WtT = Σ(Fuel Mass × LCV × WtT × RWD) / Total Energy Input",
      subFormulas: [
        "Fuel Mass (g) = Consumption (kg) × 1,000,000",
        "Total Energy = Σ(Fuel Mass × LCV × RWD) + Shore Power Energy (MJ)",
        "Shore Power from VesselPerformance.shorePowerEnergy",
        "RWD = 2.0 for RFNBO fuels, 1.0 otherwise"
      ],
      icon: <Fuel className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "tank-to-wake-calculation",
      title: "Incinerator Tank-to-Wake (TtW) Calculation",
      description: "Calculates combustion emissions including methane slip for incinerator",
      formula: "TtW = Σ[Fuel Mass × ((1-CSlip%)×CO₂eq_TtW + (CSlip%×28))] / Total Energy Input",
      subFormulas: [
        "CO₂eq_TtW = (CF_CO₂ × GWP_CO₂) + (CF_CH₄ × GWP_CH₄) + (CF_N₂O × GWP_N₂O)",
        "GWP_CO₂ = 1, GWP_CH₄ = 28, GWP_N₂O = 298",
        "CSlip = Carbon slip percentage for each fuel",
        "Shore Power Energy included in denominator"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "ghg-intensity-calculation",
      title: "Incinerator GHG Intensity Calculation",
      description: "Calculates total GHG intensity in g CO₂e/MJ including renewable fuel rewards",
      formula: "GHG Intensity = Reward Factor × (WtT + TtW)",
      subFormulas: [
        "Reward Factor Wind = 1.0 (default)",
        "GHG Intensity = 1.0 × (WtT + TtW)",
        "Units: g CO₂e/MJ"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "conversion-to-mt",
      title: "Conversion to Metric Tons (MT)",
      description: "Converts incinerator emissions from kilograms to metric tons for reporting",
      formula: "Emissions (MT/day) = Emissions (kg) ÷ 1000",
      subFormulas: [
        "CO₂_MT = Total CO₂ (kg) ÷ 1000",
        "CH₄_MT = Total CH₄ (kg) ÷ 1000",
        "N₂O_MT = Total N₂O (kg) ÷ 1000",
        "GHG reported in g CO₂e/MJ (no conversion needed)"
      ],
      icon: <Database className="w-5 h-5" />,
      borderColor: "border-gray-200",
      bgColor: "bg-gray-50",
      textColor: "text-gray-700",
    }
  ];

  const constants = [
    {
      name: "Global Warming Potentials (GWP)",
      values: [
        "GWP_CO₂ = 1",
        "GWP_CH₄ = 28", 
        "GWP_N₂O = 298"
      ]
    },
    {
      name: "Lower Calorific Values (LCV)",
      values: [
        "LCV_HFO = 40.5 MJ/kg (default)",
        "LCV_LFO = 41.5 MJ/kg (default)",
        "LCV_MGO = 42.7 MJ/kg (default)"
      ]
    },
    {
      name: "Reward Factors & Parameters",
      values: [
        "Reward Factor Wind = 1.0",
        "RFNBO RWD = 2.0",
        "Non-RFNBO RWD = 1.0",
        "Mass Conversion: 1 kg = 1,000,000 g"
      ]
    }
  ];

  const dataSources = [
    {
      collection: "VesselEmissionRawData",
      fields: [
        "incineratorConsHfo (kg)",
        "incineratorConsLfo (kg)",
        "incineratorConsMgo (kg)",
        "reportDate",
        "vesselId",
        "voyageId"
      ]
    },
    {
      collection: "FuelTable",
      fields: [
        "CF_CO₂, CF_CH₄, CF_N₂O (emission factors)",
        "LCV (Lower Calorific Value)",
        "WtT (Well-to-Tank factor)",
        "CSlip (Carbon Slip %)",
        "Fuel_Class (RFNBO classification)"
      ],
      fuelTypes: ["HFO", "LFO", "MGO"]
    },
    {
      collection: "VesselPerformance",
      fields: [
        "shorePowerEnergy (MJ)",
        "recordedAt"
      ]
    }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#c5fcdd] rounded-lg">
              <Trash2 className="w-6 h-6 text-[#27ae60]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Incinerator Emission Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Waste incineration greenhouse gas analysis methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Data Sources</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">{source.collection}</h4>
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-700">Fields:</div>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {source.fields.map((field, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-gray-400">•</span>
                            <span className="font-mono text-xs">{field}</span>
                          </li>
                        ))}
                      </ul>
                      {source.fuelTypes && (
                        <>
                          <div className="text-sm font-medium text-gray-700 mt-3">Fuel Types:</div>
                          <div className="flex flex-wrap gap-2">
                            {source.fuelTypes.map((type, idx) => (
                              <span key={idx} className="px-2 py-1 bg-white text-xs rounded border">
                                {type}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Primary Formula:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Detailed Breakdown:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncineratorEmissionAnalytics;